// Passive collectors. Attach listeners, buffer lightweight samples, and produce
// a compact summary on demand. Everything is best-effort and capped so memory
// stays bounded on long-lived pages.
import {
  MouseSummary,
  TypingSummary,
  summarizeMouse,
  summarizeTyping,
} from "./signals";

const MAX_POINTS = 500;

export interface DeviceSummary {
  webdriver: boolean;
  plugins: number;
  languages: number;
  hardware_concurrency: number;
  touch: boolean;
}

export interface ScrollSummary {
  events: number;
  max_depth: number;
}

export interface SignalSummary {
  mouse: MouseSummary;
  typing: TypingSummary;
  scroll: ScrollSummary;
  timing: { time_on_page_ms: number; fill_ms: number };
  device: DeviceSummary;
}

export class Collectors {
  private points: Array<{ x: number; y: number }> = [];
  private flight: number[] = [];
  private dwell: number[] = [];
  private lastKeyUp = 0;
  private keyDownAt: Record<string, number> = {};
  private scrollEvents = 0;
  private maxDepth = 0;
  private startedAt = 0;
  private firstInputAt = 0;

  start(now: number): void {
    this.startedAt = now;
    if (typeof window === "undefined") return;
    window.addEventListener("mousemove", this.onMove, { passive: true });
    window.addEventListener("keydown", this.onKeyDown, { passive: true });
    window.addEventListener("keyup", this.onKeyUp, { passive: true });
    window.addEventListener("scroll", this.onScroll, { passive: true });
  }

  private onMove = (e: MouseEvent): void => {
    if (this.points.length >= MAX_POINTS) this.points.shift();
    this.points.push({ x: e.clientX, y: e.clientY });
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    const t = Date.now();
    if (this.firstInputAt === 0) this.firstInputAt = t;
    this.keyDownAt[e.code] = t;
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    const t = Date.now();
    const down = this.keyDownAt[e.code];
    if (down) this.dwell.push((t - down) / 1000);
    if (this.lastKeyUp) this.flight.push((t - this.lastKeyUp) / 1000);
    this.lastKeyUp = t;
  };

  private onScroll = (): void => {
    this.scrollEvents += 1;
    if (typeof document !== "undefined") {
      const h = document.body.scrollHeight - window.innerHeight;
      if (h > 0) this.maxDepth = Math.max(this.maxDepth, window.scrollY / h);
    }
  };

  private device(): DeviceSummary {
    const nav: any = typeof navigator !== "undefined" ? navigator : {};
    return {
      webdriver: !!nav.webdriver,
      plugins: nav.plugins ? nav.plugins.length : 0,
      languages: nav.languages ? nav.languages.length : 0,
      hardware_concurrency: nav.hardwareConcurrency || 0,
      touch: typeof window !== "undefined" && "ontouchstart" in window,
    };
  }

  summary(now: number): SignalSummary {
    const fillMs = this.firstInputAt ? now - this.firstInputAt : 0;
    return {
      mouse: summarizeMouse(this.points),
      typing: summarizeTyping(this.flight, this.dwell) as TypingSummary,
      scroll: { events: this.scrollEvents, max_depth: this.maxDepth },
      timing: { time_on_page_ms: now - this.startedAt, fill_ms: fillMs },
      device: this.device(),
    };
  }
}
